import React, { ReactNode, createContext, useState, useMemo } from 'react';
import { IntlProvider } from 'react-intl';

import { defaultLang } from '@/utils/helpers';
import en from '@/intl/en.json';
import nl from '@/intl/nl.json';
import fr from '@/intl/fr.json';

const flattenMessages = (
  nestedMessages: any,
  prefix = '',
): Record<string, string> => {
  if (!nestedMessages) return {};
  return Object.keys(nestedMessages).reduce<Record<string, string>>(
    (messages, key) => {
      const value = nestedMessages[key];
      const prefixedKey = prefix ? `${prefix}.${key}` : key;

      if (typeof value === 'string') {
        messages[prefixedKey] = value;
      } else {
        Object.assign(messages, flattenMessages(value, prefixedKey));
      }

      return messages;
    },
    {},
  );
};

type LocaleContextType = {
  locale: string;
  changeLocale: (locale: string) => void;
  defaultLang: string;
};

const LocaleContext = createContext<LocaleContextType>({
  locale: defaultLang,
  changeLocale: () => {},
  defaultLang,
});

type NestedMessages = {
  [key: string]: string | NestedMessages;
};

type LocaleProviderProps = {
  children: ReactNode;
  pageContext?: {
    locale?: string;
  };
};

const LocaleProvider: React.FC<LocaleProviderProps> = ({
  children,
  pageContext = {},
}) => {
  const { locale: initialLocale = defaultLang } = pageContext;
  const [currentLocale, setCurrentLocale] = useState<string>(initialLocale.trim());

  const messages: Record<string, NestedMessages> = { en, nl, fr };

  // Flatten messages only once per locale change
  const flattenedMessages = useMemo(
    () => flattenMessages(messages[currentLocale] || messages[defaultLang]),
    [currentLocale],
  );

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      locale: currentLocale,
      changeLocale: (locale: string) => setCurrentLocale(locale.trim()),
      defaultLang,
    }),
    [currentLocale],
  );

  return (
    <LocaleContext.Provider value={contextValue}>
      <IntlProvider
        locale={currentLocale}
        defaultLocale={defaultLang}
        key={currentLocale}
        messages={flattenedMessages}
      >
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
};

export { LocaleContext, LocaleProvider };
